/**
 * Per-deal Heartbeat push timeline — renders below the existing
 * Heartbeat sync panel on the deal page.
 *
 * Sources from `useHeartbeatPushTimeline(PK, SK)` which pulls every
 * `heartbeat_push` MOTHERSHIP entity under this deal, newest first.
 * Includes successful pushes, failed pushes, and intentional skips
 * (org_not_linked, no_changes_detected, etc.) so the timeline reads
 * as a full audit log.
 *
 * Visibility:
 *   - Section is hidden entirely when the org isn't in Heartbeat
 *     (no `heartbeat_account_id`) AND no push history exists. Keeps
 *     the deal page clean for prospect/internal-only deals.
 *   - When history exists for an unlinked org (legacy data), it still
 *     renders so ops can audit the trail.
 *
 * Row expand → reveals full submission_results payload (Heartbeat row
 * IDs, change types, service lines, effective dates) for debugging.
 */

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HistoryIcon from '@mui/icons-material/History';
import WarningIcon from '@mui/icons-material/Warning';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  HeartbeatPushEntity,
  HeartbeatPushTrigger,
  useHeartbeatPushTimeline,
} from 'api/aws/heartbeat/useHeartbeatPushTimeline';
import React from 'react';

interface Props {
  /** Deal's PK (org PK). */
  PK: string | undefined;
  /** Deal's SK. */
  SK: string | undefined;
  /** True when the org has a `heartbeat_account_id` — used to gate
   *  rendering of the empty state. When false and the timeline is
   *  also empty, the whole section is hidden. */
  dealInHeartbeat: boolean;
}

const HeartbeatPushTimeline: React.FC<Props> = ({
  PK,
  SK,
  dealInHeartbeat,
}) => {
  const theme = useTheme();
  const { data: pushes, isLoading, error } = useHeartbeatPushTimeline(PK, SK);

  // Hide the whole section for deals with no HB linkage and no history.
  if (!isLoading && (!pushes || pushes.length === 0) && !dealInHeartbeat) {
    return null;
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        mt: 3,
        borderRadius: 2,
        borderColor: 'divider',
        bgcolor: theme.palette.background.paper,
        // The theme centers text on every Paper (paperBaseStyles); a log reads left.
        textAlign: 'left',
      }}
    >
      <Box sx={{ p: { xs: 2.5, md: 3 } }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
          <HistoryIcon sx={{ fontSize: 22, color: 'text.secondary' }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Heartbeat push history
          </Typography>
          {pushes && pushes.length > 0 && (
            <Chip
              label={`${pushes.length} push${pushes.length === 1 ? '' : 'es'}`}
              size="small"
              sx={{ fontFamily: 'monospace' }}
            />
          )}
        </Stack>

        {isLoading && (
          <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 2 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Loading push history…
            </Typography>
          </Stack>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            Couldn't load push history: {error.message}
          </Alert>
        )}

        {!isLoading && !error && pushes && pushes.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
            No pushes have been recorded for this deal yet. Pushes will appear
            here after the next PandaDoc sign event or no signature contract
            creation.
          </Typography>
        )}

        {!isLoading && !error && pushes && pushes.length > 0 && (
          <Stack spacing={1}>
            {pushes.map((p) => (
              <PushRow key={p.SK} push={p} />
            ))}
          </Stack>
        )}
      </Box>
    </Paper>
  );
};

// ════════════════════════════════════════════════════════════════════════
// Per-push row (collapsible)
// ════════════════════════════════════════════════════════════════════════

const PushRow: React.FC<{ push: HeartbeatPushEntity }> = ({ push }) => {
  const statusColor = push.success
    ? 'success'
    : push.skip_reason
      ? 'warning'
      : 'error';
  const StatusIcon = push.success
    ? CheckCircleIcon
    : push.skip_reason
      ? WarningIcon
      : ErrorIcon;
  const statusLabel = push.success
    ? 'Pushed'
    : push.skip_reason
      ? `Skipped (${push.skip_reason})`
      : 'Failed';

  return (
    <Accordion
      disableGutters
      square
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1.5,
        '&:before': { display: 'none' },
        bgcolor: 'background.paper',
        // Accordion is a Paper, and the theme centers text on every Paper
        // (paperBaseStyles). Reset it here or each row re-centers itself.
        textAlign: 'left',
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          minHeight: 44,
          '& .MuiAccordionSummary-content': {
            my: 0.5,
            alignItems: 'center',
            // Fixed-column grid so chips line up across every row,
            // regardless of how many optional fields (trigger, etc.)
            // are present. The 1fr middle column absorbs slack so the
            // "by X" attribution stays right-flush.
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: '120px 84px 1fr auto',
              md: '120px 84px minmax(120px, 160px) 1fr auto',
            },
            columnGap: 1.25,
            rowGap: 0.5,
          },
        }}
      >
        {/* Slot 1 — Status (Pushed / Failed / Skipped) */}
        <Chip
          icon={<StatusIcon />}
          label={statusLabel}
          color={statusColor}
          size="small"
          variant={push.success ? 'filled' : 'outlined'}
          sx={{ justifySelf: 'start', maxWidth: '100%' }}
        />

        {/* Slot 2 — Flow */}
        <Box sx={{ justifySelf: 'start' }}>
          <FlowChip flow={push.flow} />
        </Box>

        {/* Slot 3 — Trigger (only on md+ so smaller screens save space).
            Renders an empty slot when the trigger is unknown so adjacent
            columns stay aligned across rows. */}
        <Box
          sx={{
            justifySelf: 'start',
            display: { xs: 'none', md: 'block' },
          }}
        >
          {push.trigger !== 'unknown' && <TriggerChip trigger={push.trigger} />}
        </Box>

        {/* Slot 4 — Date + submission count (left-aligned in the 1fr slot) */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          flexWrap="wrap"
          useFlexGap
          sx={{ minWidth: 0 }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: 13 }}
          >
            {formatTimestamp(push.pushed_at)}
          </Typography>
          {push.submission_results.length > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
              · {push.submission_results.length} submission
              {push.submission_results.length === 1 ? '' : 's'}
            </Typography>
          )}
        </Stack>

        {/* Slot 5 — Pushed by (right-flush) */}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            justifySelf: { xs: 'start', sm: 'end' },
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {push.pushed_by ?? ''}
        </Typography>
      </AccordionSummary>

      <AccordionDetails
        sx={{
          px: { xs: 2, md: 2.5 },
          py: 2,
          borderTop: 1,
          borderColor: 'divider',
        }}
      >
        {isLegacyRecord(push) && (
          <Alert severity="info" variant="outlined" sx={{ mb: 2, py: 0 }}>
            Logged before push details were recorded, so only the outcome and
            context were kept. The submissions themselves are on the account in
            Heartbeat.
          </Alert>
        )}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '140px minmax(0, 1fr)' },
            columnGap: 3,
            rowGap: { xs: 0.5, sm: 2 },
            alignItems: 'start',
          }}
        >
          {push.contract_result && (
            <DetailRow label="Contract">
              <ContractSummary result={push.contract_result} />
            </DetailRow>
          )}

          {push.submission_results.length > 0 && (
            <DetailRow label="Submissions">
              {isLegacyRecord(push) ? (
                <Typography variant="body2">
                  {push.submission_results.length} sent
                </Typography>
              ) : (
                <SubmissionList
                  results={push.submission_results}
                  pushSucceeded={push.success}
                />
              )}
            </DetailRow>
          )}

          {hasContextInfo(push) && (
            <DetailRow label="Context">
              <ContextList push={push} />
            </DetailRow>
          )}
        </Box>

        {push.error_message && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {push.error_message}
          </Alert>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

// ── Expanded-row sub-components ─────────────────────────────────────────

/** One label/value row in the expanded panel's two-column grid. */
const DetailRow: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => {
  return (
    <>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ fontWeight: 500, pt: { sm: 0.25 }, mt: { xs: 1.5, sm: 0 } }}
      >
        {label}
      </Typography>
      <Box sx={{ minWidth: 0 }}>{children}</Box>
    </>
  );
};

const ContractSummary: React.FC<{
  result: NonNullable<HeartbeatPushEntity['contract_result']>;
}> = ({ result }) => {
  const status =
    result.id != null
      ? `#${result.id}`
      : result.success
        ? 'Created (id not recorded)'
        : 'Not created';
  return (
    <Stack spacing={1} alignItems="flex-start">
      <Typography variant="body2">
        <Box
          component="span"
          sx={{
            fontFamily: result.id != null ? 'monospace' : undefined,
            fontWeight: 500,
            color: result.success ? 'text.primary' : 'error.main',
          }}
        >
          {status}
        </Box>
        {result.start_date && (
          <Box component="span" sx={{ color: 'text.secondary' }}>
            {'  ·  '}
            {shortDate(result.start_date)} to {shortDate(result.end_date)}
          </Box>
        )}
      </Typography>
      {result.error && (
        <Typography variant="body2" color="error.main">
          {result.error}
        </Typography>
      )}
      {result.service_budget && <ServiceBudget budget={result.service_budget} />}
    </Stack>
  );
};

/** Grobotic-side context as a compact key/value list. */
const ContextList: React.FC<{ push: HeartbeatPushEntity }> = ({ push }) => {
  const rows: Array<[string, string, boolean?]> = [];
  if (push.heartbeat_account_id != null) rows.push(['Account', `#${push.heartbeat_account_id}`, true]);
  if (push.account_source) rows.push(['Account source', push.account_source.replace(/_/g, ' ')]);
  if (push.predecessor_source) rows.push(['Predecessor', push.predecessor_source]);
  if (push.predecessor_deal_sk) rows.push(['Predecessor SK', push.predecessor_deal_sk, true]);
  if (push.submitter_heartbeat_user_id != null) {
    rows.push(['Submitter', `Heartbeat user ${push.submitter_heartbeat_user_id}`]);
  }
  if (push.team_members_attached != null) {
    rows.push([
      'Team attached',
      `${push.team_members_attached}${push.team_members_skipped_unknown_user ? ` (skipped ${push.team_members_skipped_unknown_user})` : ''}`,
    ]);
  }
  if (push.parent_account_created) rows.push(['Parent account', 'created']);
  if (push.renewal_context) rows.push(['Renewal', renewalLabel(push.renewal_context)]);

  return (
    <Box
      component="dl"
      sx={{
        m: 0,
        display: 'grid',
        gridTemplateColumns: 'max-content minmax(0, 1fr)',
        columnGap: 2,
        rowGap: 0.5,
      }}
    >
      {rows.map(([label, value, mono]) => (
        <React.Fragment key={label}>
          <Typography component="dt" variant="body2" color="text.secondary">
            {label}
          </Typography>
          <Typography
            component="dd"
            variant="body2"
            sx={{ m: 0, fontFamily: mono ? 'monospace' : undefined, overflowWrap: 'anywhere' }}
          >
            {value}
          </Typography>
        </React.Fragment>
      ))}
    </Box>
  );
};

const compactTableBoxSx = {
  bgcolor: 'background.paper',
  border: 1,
  borderColor: 'divider',
  borderRadius: 1,
  overflow: 'hidden',
  // Sizes table to content so columns don't sprawl when expanded.
  display: 'inline-block',
  maxWidth: '100%',
} as const;

const compactTableSx = {
  width: 'auto',
  '& th, & td': {
    fontSize: 13,
    py: 0.75,
    px: 1.5,
    textAlign: 'left',
    whiteSpace: 'nowrap',
    borderBottom: 'none',
  },
  '& th': {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: 'text.secondary',
    bgcolor: 'action.hover',
    borderBottom: 1,
    borderBottomColor: 'divider',
  },
} as const;

/** Single-deal submission list. Renders as a compact table that sizes
 *  itself to content (not full-width) so the columns stay readable. */
const SubmissionList: React.FC<{
  results: HeartbeatPushEntity['submission_results'];
  pushSucceeded: boolean;
}> = ({ results, pushSucceeded }) => {
  // Status column is only worth showing when something might be
  // non-green to point at. If the overall push succeeded, every
  // submission in this list landed — drop the column entirely.
  const showStatus = !pushSucceeded || results.some((r) => !r.success);
  return (
    <Box sx={compactTableBoxSx}>
      <Table size="small" sx={compactTableSx}>
        <TableHead>
          <TableRow>
            <TableCell>HB ID</TableCell>
            <TableCell>Change</TableCell>
            <TableCell>Service line</TableCell>
            <TableCell>Effective</TableCell>
            <TableCell>MRR</TableCell>
            {showStatus && <TableCell>Status</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {results.map((r, i) => (
            <TableRow key={i}>
              <TableCell sx={{ fontFamily: 'monospace' }}>
                {r.id == null ? '—' : `#${r.id}`}
                {r.already_existed && (
                  <Tooltip title="Heartbeat matched an existing submission; nothing new was recorded">
                    <Typography component="span" variant="caption" color="warning.main">
                      {' '}existing
                    </Typography>
                  </Tooltip>
                )}
              </TableCell>
              <TableCell>
                <Tooltip title={r.submission_type ?? ''}>
                  <span>{r.change_type ?? r.submission_type ?? '—'}</span>
                </Tooltip>
              </TableCell>
              <TableCell>{r.service_line ?? '—'}</TableCell>
              <TableCell sx={{ fontFamily: 'monospace' }}>
                {r.effective_date ? shortDate(r.effective_date) : '—'}
                {r.length_of_change_months != null && (
                  <Typography component="span" variant="caption" color="text.secondary">
                    {' '}{r.length_of_change_months}mo
                  </Typography>
                )}
              </TableCell>
              <TableCell sx={{ fontFamily: 'monospace' }}>
                {r.current_mrr != null && r.new_mrr != null
                  ? `${money(r.current_mrr)} → ${money(r.new_mrr)}`
                  : '—'}
              </TableCell>
              {showStatus && (
                <TableCell>
                  {r.success ? (
                    <CheckCircleIcon
                      sx={{ fontSize: 14, color: 'success.main' }}
                    />
                  ) : (
                    <Tooltip title={r.error ?? 'failed'}>
                      <ErrorIcon sx={{ fontSize: 14, color: 'error.main' }} />
                    </Tooltip>
                  )}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

/** Service MRR per line as the Flow A contract sent it. This is all the
 *  contract carries; subscription rows come from NetSuite, not this push. */
const ServiceBudget: React.FC<{ budget: Record<string, number> }> = ({
  budget,
}) => {
  const lines = Object.entries(budget);
  if (lines.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        No service lines sent
      </Typography>
    );
  }
  return (
    <Box sx={compactTableBoxSx}>
      <Table size="small" sx={compactTableSx}>
        <TableHead>
          <TableRow>
            <TableCell>Service line</TableCell>
            <TableCell>Monthly service MRR</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {lines.map(([line, rate]) => (
            <TableRow key={line}>
              <TableCell>{line}</TableCell>
              <TableCell sx={{ fontFamily: 'monospace' }}>{money(rate)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

/** Pushes logged before the logger read the right result fields saved every
 *  submission as nulls. Nothing to show for those beyond the count. */
function isLegacyRecord(push: HeartbeatPushEntity): boolean {
  return push.submission_results.every(
    (r) => r.success && r.id == null && r.change_type == null && r.submission_type == null,
  );
}

/** Whether any context field has a value worth rendering. Skipping the
 *  whole Context section is cleaner than rendering an empty Stack with
 *  just a label. */
function hasContextInfo(push: HeartbeatPushEntity): boolean {
  return Boolean(
    push.heartbeat_account_id != null ||
    push.account_source ||
    push.predecessor_source ||
    push.predecessor_deal_sk ||
    push.submitter_heartbeat_user_id != null ||
    push.team_members_attached != null ||
    push.parent_account_created ||
    push.renewal_context
  );
}

// ── Chips ───────────────────────────────────────────────────────────────

const FlowChip: React.FC<{ flow: HeartbeatPushEntity['flow'] }> = ({
  flow,
}) => {
  if (flow === 'none') return null;
  const colorByFlow: Record<string, 'primary' | 'info' | 'secondary'> = {
    A: 'primary',
    B: 'info',
    IO: 'secondary',
  };
  return (
    <Tooltip
      title={
        flow === 'A'
          ? 'Flow A — new contract (Heartbeat receives a Contract + Subscriptions)'
          : flow === 'B'
            ? 'Flow B — service-change submissions diffed against the predecessor'
            : 'Flow IO — Insertion Order, one Increase per catalog product'
      }
    >
      <Chip
        label={`Flow ${flow}`}
        size="small"
        color={colorByFlow[flow] ?? 'default'}
        variant="outlined"
        sx={{ fontFamily: 'monospace', fontWeight: 600 }}
      />
    </Tooltip>
  );
};

const TriggerChip: React.FC<{ trigger: HeartbeatPushTrigger }> = ({
  trigger,
}) => {
  const label =
    trigger === 'pandadoc_webhook'
      ? 'PandaDoc webhook'
      : trigger === 'dev_button'
        ? 'Dev push'
        : trigger === 'no_signature_flow'
          ? 'No-signature flow'
          : trigger === 'bulk_patch'
            ? 'Bulk patch'
            : trigger === 'cli'
              ? 'CLI'
              : 'Unknown';
  return (
    <Chip
      label={label}
      size="small"
      variant="outlined"
      sx={{ fontFamily: 'monospace', bgcolor: 'action.hover' }}
    />
  );
};

// ── Helpers ─────────────────────────────────────────────────────────────

function renewalLabel(
  ctx: NonNullable<HeartbeatPushEntity['renewal_context']>,
): string {
  const version = ctx.predecessor_semantic_version ?? ctx.predecessor_deal_sk;
  if (ctx.reason === 'no_hb_contract') {
    return `no Heartbeat contract yet, v${version} backfilled as history`;
  }
  return `predecessor v${version} ended ${shortDate(ctx.predecessor_end_date ?? undefined)}`;
}

function shortDate(iso: string | undefined): string {
  return iso ? iso.slice(0, 10) : '?';
}

function money(n: number): string {
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatTimestamp(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export default HeartbeatPushTimeline;
